/** Shared Beat Shop preview player — tiles, detail, and SoundCloud-style mini bar. */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { trackBeatPreviewed } from './analytics';

/**
 * Playback stops at min(clip duration, PREVIEW_PLAYBACK_SECONDS) rather than at
 * a fixed constant: the server generates 60s previews, older beats still carry
 * 45s ones, and sellers may supply their own of any length. A hardcoded limit
 * truncated or mislabelled all but one of those.
 *
 * The ceiling mirrors the backend's PREVIEW_SECONDS (beats/preview.js). Uploads
 * are allowed up to MAX_PREVIEW_SECONDS (90s), which is deliberately longer, so
 * this is what keeps that extra slack from being playable. It doubles as the
 * assumed length until loadedmetadata reports the real one.
 */
export const PREVIEW_PLAYBACK_SECONDS = 60;

export type PreviewTrack = {
  id: string;
  name: string;
  preview_url: string;
  bpm?: number;
  musical_key?: string;
  seller_name?: string | null;
  is_favorited: boolean;
};

type PreviewPlayerContextValue = {
  track: PreviewTrack | null;
  playing: boolean;
  currentTime: number;
  progress: number;
  /** Effective playable length of the current clip, in seconds. */
  limit: number;
  queue: PreviewTrack[];
  canPrev: boolean;
  canNext: boolean;
  /** Replace the Shop queue (filtered list). Empty clears queue mode. */
  setQueue: (tracks: PreviewTrack[]) => void;
  play: (next: PreviewTrack) => void;
  pause: () => void;
  toggle: (next?: PreviewTrack) => void;
  playPrev: () => void;
  playNext: () => void;
  seek: (frac: number) => void;
  stop: () => void;
  setFavorited: (beatId: string, favorited: boolean) => void;
  isCurrent: (beatId: string) => boolean;
  isPlayingId: (beatId: string) => boolean;
};

const PreviewPlayerContext = createContext<PreviewPlayerContextValue | null>(null);

export function usePreviewPlayer(): PreviewPlayerContextValue {
  const ctx = useContext(PreviewPlayerContext);
  if (!ctx) throw new Error('usePreviewPlayer must be used within PreviewPlayerProvider');
  return ctx;
}

/** Optional — returns null outside PreviewPlayerProvider. */
export function usePreviewPlayerOptional(): PreviewPlayerContextValue | null {
  return useContext(PreviewPlayerContext);
}

export const PreviewPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [track, setTrack] = useState<PreviewTrack | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [limit, setLimit] = useState(PREVIEW_PLAYBACK_SECONDS);
  const [queue, setQueueState] = useState<PreviewTrack[]>([]);

  // read by play()/seek(), which run outside the audio element's own closures
  const limitRef = useRef(PREVIEW_PLAYBACK_SECONDS);
  const trackRef = useRef<PreviewTrack | null>(null);
  const queueRef = useRef<PreviewTrack[]>([]);
  const playRef = useRef<(next: PreviewTrack) => void>(() => {});

  useEffect(() => { trackRef.current = track; }, [track]);
  useEffect(() => { queueRef.current = queue; }, [queue]);

  const detach = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.src = '';
    audioRef.current = null;
  }, []);

  useEffect(() => () => { detach(); }, [detach]);

  const playNextFromQueue = useCallback(() => {
    const q = queueRef.current;
    const current = trackRef.current;
    if (!current || q.length === 0) return;
    const idx = q.findIndex((t) => t.id === current.id);
    if (idx < 0 || idx >= q.length - 1) return;
    const next = q[idx + 1];
    if (next?.preview_url) playRef.current(next);
  }, []);

  const bindAudio = useCallback((url: string) => {
    detach();
    const audio = new Audio(url);
    audioRef.current = audio;
    // local so the clamp never reads a stale render value; mirrored into
    // limitRef/state for consumers
    let effectiveLimit = PREVIEW_PLAYBACK_SECONDS;
    limitRef.current = effectiveLimit;
    setLimit(effectiveLimit);
    let advanced = false;
    const advanceOnce = () => {
      if (advanced) return;
      advanced = true;
      playNextFromQueue();
    };
    const onMeta = () => {
      const d = audio.duration;
      if (!Number.isFinite(d) || d <= 0) return;
      effectiveLimit = Math.min(d, PREVIEW_PLAYBACK_SECONDS);
      limitRef.current = effectiveLimit;
      setLimit(effectiveLimit);
    };
    const onTime = () => {
      setCurrentTime(audio.currentTime);
      if (audio.currentTime >= effectiveLimit) {
        audio.pause();
        audio.currentTime = effectiveLimit;
        setCurrentTime(effectiveLimit);
        setPlaying(false);
        advanceOnce();
      }
    };
    const onEnd = () => {
      setPlaying(false);
      advanceOnce();
    };
    const onPause = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('play', onPlay);
    return () => {
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('play', onPlay);
    };
  }, [detach, playNextFromQueue]);

  const unbindRef = useRef<(() => void) | null>(null);

  const play = useCallback((next: PreviewTrack) => {
    if (!next.preview_url) return;
    unbindRef.current?.();
    unbindRef.current = bindAudio(next.preview_url);
    setTrack(next);
    trackRef.current = next;
    setCurrentTime(0);
    const audio = audioRef.current;
    if (!audio) return;
    audio.play().then(() => trackBeatPreviewed(next.id)).catch(() => {});
  }, [bindAudio]);

  useEffect(() => { playRef.current = play; }, [play]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const toggle = useCallback((next?: PreviewTrack) => {
    if (!next) {
      if (playing) pause();
      else if (track) play(track);
      return;
    }
    if (track?.id === next.id && playing) pause();
    else play(next);
  }, [pause, play, playing, track]);

  const setQueue = useCallback((tracks: PreviewTrack[]) => {
    const playable = tracks.filter((t) => !!t.preview_url);
    setQueueState(playable);
    queueRef.current = playable;
  }, []);

  const queueIndex = useMemo(() => {
    if (!track || queue.length === 0) return -1;
    return queue.findIndex((t) => t.id === track.id);
  }, [track, queue]);

  const canPrev = queueIndex > 0;
  const canNext = queueIndex >= 0 && queueIndex < queue.length - 1;

  const playPrev = useCallback(() => {
    const q = queueRef.current;
    const current = trackRef.current;
    if (!current || q.length === 0) return;
    const idx = q.findIndex((t) => t.id === current.id);
    if (idx <= 0) return;
    play(q[idx - 1]);
  }, [play]);

  const playNext = useCallback(() => {
    const q = queueRef.current;
    const current = trackRef.current;
    if (!current || q.length === 0) return;
    const idx = q.findIndex((t) => t.id === current.id);
    if (idx < 0 || idx >= q.length - 1) return;
    play(q[idx + 1]);
  }, [play]);

  const seek = useCallback((frac: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const t = Math.max(0, Math.min(1, frac)) * limitRef.current;
    audio.currentTime = t;
    setCurrentTime(t);
  }, []);

  const stop = useCallback(() => {
    unbindRef.current?.();
    unbindRef.current = null;
    detach();
    setTrack(null);
    trackRef.current = null;
    setPlaying(false);
    setCurrentTime(0);
  }, [detach]);

  const setFavorited = useCallback((beatId: string, favorited: boolean) => {
    setTrack((prev) => (prev && prev.id === beatId ? { ...prev, is_favorited: favorited } : prev));
    setQueueState((prev) => prev.map((t) => (t.id === beatId ? { ...t, is_favorited: favorited } : t)));
  }, []);

  const value = useMemo<PreviewPlayerContextValue>(() => ({
    track,
    playing,
    currentTime,
    progress: Math.min(1, currentTime / limit),
    limit,
    queue,
    canPrev,
    canNext,
    setQueue,
    play,
    pause,
    toggle,
    playPrev,
    playNext,
    seek,
    stop,
    setFavorited,
    isCurrent: (id) => track?.id === id,
    isPlayingId: (id) => track?.id === id && playing,
  }), [
    track, playing, currentTime, limit, queue, canPrev, canNext,
    setQueue, play, pause, toggle, playPrev, playNext, seek, stop, setFavorited,
  ]);

  return (
    <PreviewPlayerContext.Provider value={value}>
      {children}
    </PreviewPlayerContext.Provider>
  );
};
